import { createContext, useContext, useEffect, useState } from 'react';
import type { AuthData, User } from '../types/user';
import { getUser, isAuthenticated, clearAuthData, setAuthData } from '../services/auth';
import { sleep } from '@/utils/sleep';

export interface AuthContext {
  user: User | undefined;
  isAuth: boolean;
  logout: () => Promise<void>;
  login: (authData: AuthData) => void;
}

const AuthContext = createContext<AuthContext | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | undefined>(getUser());
  const [isAuth, setIsAuth] = useState(isAuthenticated());

  useEffect(() => {
    const checkAuth = () => {
      const currentUser = getUser();
      const currentIsAuth = isAuthenticated();
      setUser(currentUser);
      setIsAuth(currentIsAuth);
    };

    // Check auth on mount
    checkAuth();

    // Listen for storage changes
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const logout = async() => {
    clearAuthData();
    setUser(undefined);
    setIsAuth(false);
    await sleep(200);
  };

  const login = async (authData: AuthData) => {
    setUser(authData.user);
    setIsAuth(true);
    setAuthData(authData)
    await sleep(200);
  };

  return (
    <AuthContext.Provider value={{ user, isAuth, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};